// Weekly standing slots — admin API (routes/standing.ts) and shared shapes.
import { supabase } from '../../utils/supabase';
import { API_BASE_URL } from '../../utils/apiConfig';

export class StandingApiError extends Error {
  code?: string;
  details?: any;
  constructor(message: string, code?: string, details?: any) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

export async function standingApi<T = any>(path: string, init: { method?: string; body?: any } = {}): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new StandingApiError('Not signed in');
  const res = await fetch(`${API_BASE_URL}/api/standing${path}`, {
    method: init.method ?? 'GET',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new StandingApiError(json.error || 'Something went wrong', json.code, json.details);
  return json.data as T;
}

export interface WeeklyTime { weekday: number; startTime: string }
export interface SlotClash { date: string; reason: 'off' | 'leave' | 'busy' | 'room' | 'member' }
export interface SlotGrid {
  scheduling: 'fixed_recurring' | 'member_choice';
  slots: { min: number; max: number };
  durationMin: number;
  trainer: { id: string; name: string; photoUrl: string | null };
  bookable: { id: string; name: string };
  branches: Array<{ id: string; name: string }>;
  branchId: string;
  window: { from: string; to: string; capped: boolean; weeks: number };
  today: string;
  days: Array<{ weekday: number; dates: number; times: Array<{ startTime: string; clashes: SlotClash[] }> }>;
  current: WeeklyTime[];
}
export interface StandingPick { times: WeeklyTime[]; branchId: string; acceptClashes?: boolean }

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const dayMonth = (ymd: string) => `${Number(ymd.slice(8, 10))} ${MON[Number(ymd.slice(5, 7)) - 1]}`;
export function time12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}
export const slotText = (t: WeeklyTime) => `${WEEKDAYS[t.weekday]} ${time12(t.startTime)}`;
export const CLASH_REASON: Record<SlotClash['reason'], string> = {
  off: 'not working', leave: 'on leave', busy: 'booked', room: 'no room free', member: 'member booked',
};
